import { FC } from 'react';

import {
  AssessmentAnswer,
  AssessmentQuestionType,
  CompletedProgramAssessmentsDataQuery,
  MultiSignatureAssessmentQuestion,
  ProgramAssessmentDataFragment,
  MultipleChoiceScoredAssessmentAnswer,
  MultipleChoiceAssessmentAnswer,
  MultiSelectAssessmentAnswer,
  FreeTextAssessmentAnswer,
  RichTextAssessmentAnswer,
  SignatureAssessmentAnswer,
  MultiSignatureAssessmentAnswer,
  InitialsAssessmentAnswer,
  FileUploadAssessmentAnswer,
  PatientContactAssessmentAnswer,
} from '../../../../generated/graphql';

import { ProgramAssessmentQuestion } from '../../../lib/program-assessment';
import { createQuestionsForDisplay } from '../../../lib/assessment-responses';

import SlideoverSection from '../../SlideoverSection';
import QuestionItem from '../QuestionItem';
import CompletedSignature from '../../CompletedSignature';
import { findAnswerForQuestion, filterEmptyFollowUpAnswers } from './helpers';
import {
  isAssessmentAForm,
  isMultiSelectAssessmentQuestion,
  isStatementAssessmentQuestion,
} from '../../../lib/assessments';
import { AssessmentSchemaType } from '../../../../generated/graphql';
import { getAnswerValue, getAnswerQuestion } from '../../../lib/answerHelpers';
import { QuestionSubscale } from '../QuestionsSection';
import FileInfoDisplay from '../FileInfoDisplay';
import classNames from 'classnames';
import { formatPatientContactSnapshotAnswer } from '../../../lib/patientContactLabels';

const ResponsesSection: FC<{
  title?: string;
  questionFrame?: string;
  programAssessment?:
    | ProgramAssessmentDataFragment
    | CompletedProgramAssessmentsDataQuery['completedProgramAssessmentsByAssessment'][number];
}> = ({ title, questionFrame, programAssessment }) => {
  const questions = programAssessment?.assessment
    ?.questions as ProgramAssessmentQuestion[];
  const rawAnswers = programAssessment?.answers as AssessmentAnswer[];
  // Empty follow-up rows filtered only for orphaned-answer text fallback lookup.
  // Primary rendering uses matchedAnswer from rawAnswers via createQuestionsForDisplay.
  const filteredAnswersForFallback = filterEmptyFollowUpAnswers(
    rawAnswers ?? [],
    questions,
  );

  // Treat NonScored assessments like forms for display purposes: no auto-numbered
  // rows, statement-as-section-header rendering. They have no clinical scoring
  // and read more like intake questionnaires than standard assessments.
  const isNonScored =
    programAssessment?.assessment?.schemaType ===
    AssessmentSchemaType.NonScored;
  const isForm =
    isAssessmentAForm(programAssessment?.assessment) || isNonScored;

  const noClientAnswers = rawAnswers?.length === 0;

  // Pair answers to questions by index (not question text) so duplicate prompts
  // like "If Yes, please describe:" map to the correct response.
  const orderedQuestions = createQuestionsForDisplay(questions, rawAnswers);

  return (
    <SlideoverSection
      title={title}
      {...(noClientAnswers && {
        TitleRightComponent: (
          <div className="text-small-caption text-neutral-110">
            Waiting for completion
          </div>
        ),
      })}
    >
      {questionFrame && Boolean(rawAnswers?.length) && (
        <div className="mb-4 font-sans text-caption text-neutral-125">
          {questionFrame}
        </div>
      )}
      {orderedQuestions.map(
        (
          { question: templateQuestion, questionText, matchedAnswer },
          questionIndex,
        ) => {
          const isLastQuestion = questionIndex === orderedQuestions.length - 1;
          const answer =
            matchedAnswer ??
            findAnswerForQuestion(filteredAnswersForFallback, {
              question: questionText,
              questionType:
                templateQuestion?.questionType ||
                AssessmentQuestionType.FreeText,
            });

          // For NonScored assessments every question is optional. Hide rows where
          // the client skipped the question so provider-facing responses don't
          // fill with empty placeholders (e.g. the opt-out question).
          if (
            isNonScored &&
            templateQuestion?.questionType !== AssessmentQuestionType.Statement
          ) {
            const mcAnswer = answer as
              | MultipleChoiceAssessmentAnswer
              | undefined;
            const msAnswer = answer as MultiSelectAssessmentAnswer | undefined;
            const ftAnswer = answer as FreeTextAssessmentAnswer | undefined;
            const isEmpty =
              !answer ||
              (answer.questionType === AssessmentQuestionType.MultipleChoice &&
                !mcAnswer?.answer) ||
              (answer.questionType === AssessmentQuestionType.MultiSelect &&
                !(msAnswer?.answers && msAnswer.answers.length > 0)) ||
              (answer.questionType === AssessmentQuestionType.FreeText &&
                !ftAnswer?.answer);
            if (isEmpty) {
              return null;
            }
          }

          if (
            templateQuestion?.questionType ===
            AssessmentQuestionType.MultiSignature
          ) {
            const multiSignatureQuestion =
              templateQuestion as MultiSignatureAssessmentQuestion;

            return (
              <QuestionItem
                key={questionIndex}
                isForm={isForm}
                questionIndex={questionIndex}
                question={questionText}
                className={classNames(
                  'w-full pt-2',
                  isLastQuestion ? 'mb-0' : '',
                )}
                subscale={
                  !Boolean(
                    multiSignatureQuestion?.existingSignatures?.length,
                  ) && QuestionSubscale.MultiSignature
                }
                additionalAnswerElem={
                  <div className="mt-2">
                    {multiSignatureQuestion.existingSignatures?.map(
                      (signature, signatureIndex) => (
                        <CompletedSignature
                          key={signatureIndex}
                          signatureName={signature.signatureName}
                          signatureRole={signature.signatureRole}
                          signedAt={new Date(signature.signedAt).toISOString()}
                          className={classNames(
                            'font-normal',
                            signatureIndex !== 0 ? 'mt-4' : 'mt-2',
                          )}
                          size="small"
                        />
                      ),
                    )}
                  </div>
                }
              />
            );
          }

          if (answer) {
            switch (answer.questionType) {
              case AssessmentQuestionType.MultipleChoiceScored:
                const multipleChoiceScoredAnswer =
                  answer as MultipleChoiceScoredAssessmentAnswer;
                return (
                  <QuestionItem
                    key={questionIndex}
                    isForm={isForm}
                    questionIndex={questionIndex}
                    question={multipleChoiceScoredAnswer.question}
                    subscale={multipleChoiceScoredAnswer.subscale}
                    answer={multipleChoiceScoredAnswer.answer}
                    className={classNames(isLastQuestion && 'mb-0')}
                  />
                );
              case AssessmentQuestionType.MultipleChoice:
                const multipleChoiceAnswer =
                  answer as MultipleChoiceAssessmentAnswer;
                return (
                  <QuestionItem
                    key={questionIndex}
                    isForm={isForm}
                    questionIndex={questionIndex}
                    question={multipleChoiceAnswer.question}
                    answer={multipleChoiceAnswer.answer}
                    className={classNames(
                      'w-full pt-2',
                      isLastQuestion ? 'mb-0' : '',
                    )}
                  />
                );
              case AssessmentQuestionType.MultiSelect:
                const multiSelectAnswer = answer as MultiSelectAssessmentAnswer;
                const multiSelectOptionLabels =
                  templateQuestion &&
                  isMultiSelectAssessmentQuestion(templateQuestion)
                    ? templateQuestion.answerOptions?.map(
                        (option) => option.answerOption,
                      ) ?? []
                    : [];
                const hasMultiSelectRows =
                  multiSelectOptionLabels.length > 0 ||
                  multiSelectAnswer.answers.length > 0;
                return (
                  <QuestionItem
                    key={questionIndex}
                    isForm={isForm}
                    questionIndex={questionIndex}
                    question={multiSelectAnswer.question}
                    questionType={AssessmentQuestionType.MultiSelect}
                    answerOptions={
                      hasMultiSelectRows ? multiSelectOptionLabels : undefined
                    }
                    selectedAnswers={multiSelectAnswer.answers}
                    answer={hasMultiSelectRows ? undefined : 'Nothing selected'}
                    className={classNames(
                      'w-full pt-2',
                      isLastQuestion ? 'mb-0' : '',
                    )}
                  />
                );
              case AssessmentQuestionType.FreeText:
                const freeTextAnswer = answer as FreeTextAssessmentAnswer;
                return (
                  <QuestionItem
                    key={questionIndex}
                    isForm={isForm}
                    questionIndex={questionIndex}
                    question={freeTextAnswer.question}
                    answer={freeTextAnswer.answer}
                    className={classNames(
                      'w-full pt-2',
                      isLastQuestion ? 'mb-0' : '',
                    )}
                  />
                );
              case AssessmentQuestionType.Initials:
                const initialsAnswer = answer as InitialsAssessmentAnswer;
                return (
                  <QuestionItem
                    key={questionIndex}
                    isForm={isForm}
                    questionIndex={questionIndex}
                    question={initialsAnswer.question}
                    className={classNames(
                      'w-full pt-2',
                      isLastQuestion ? 'mb-0' : '',
                    )}
                    answerClassName="font-normal"
                    subscale={
                      !initialsAnswer.signatureName
                        ? QuestionSubscale.Initials
                        : undefined
                    }
                    additionalAnswerElem={
                      initialsAnswer.signatureName && (
                        <CompletedSignature
                          hideNameAndRole
                          signatureName={initialsAnswer.signatureName}
                          signedAt={initialsAnswer.signedAt}
                          size="small"
                          signatureRole="Client"
                        />
                      )
                    }
                  />
                );
              case AssessmentQuestionType.Signature:
                const signatureAnswer = answer as SignatureAssessmentAnswer;
                return (
                  <QuestionItem
                    key={questionIndex}
                    isForm={isForm}
                    questionIndex={questionIndex}
                    question={signatureAnswer.question}
                    className={classNames(
                      'w-full pt-2',
                      isLastQuestion ? 'mb-0' : '',
                    )}
                    answerClassName="font-normal"
                    subscale={
                      !signatureAnswer.signatureName
                        ? QuestionSubscale.Signature
                        : undefined
                    }
                    additionalAnswerElem={
                      signatureAnswer.signatureName && (
                        <CompletedSignature
                          signatureName={signatureAnswer.signatureName}
                          signedAt={signatureAnswer.signedAt}
                          size="small"
                          signatureRole="Client"
                        />
                      )
                    }
                  />
                );
              case AssessmentQuestionType.MultiSignature:
                const multiSignatureAnswer =
                  answer as MultiSignatureAssessmentAnswer;
                return (
                  <QuestionItem
                    key={questionIndex}
                    isForm={isForm}
                    questionIndex={questionIndex}
                    question={multiSignatureAnswer.question}
                    className={classNames(
                      'w-full pt-2',
                      isLastQuestion ? 'mb-0' : '',
                    )}
                    answerClassName="font-normal"
                    additionalAnswerElem={
                      <div className="mt-3">
                        {multiSignatureAnswer.signatures.map(
                          (signature, signatureIndex) => (
                            <CompletedSignature
                              key={signatureIndex}
                              signatureName={signature.signatureName}
                              signatureRole={signature.signatureRole}
                              signedAt={signature.signedAt}
                              className="mb-[6px]"
                              size="small"
                            />
                          ),
                        )}
                      </div>
                    }
                  />
                );
              case AssessmentQuestionType.RichText:
                const richTextAnswer = answer as RichTextAssessmentAnswer;
                return (
                  <QuestionItem
                    key={questionIndex}
                    isForm={isForm}
                    questionIndex={questionIndex}
                    question={richTextAnswer.question}
                    answer={richTextAnswer.answer}
                    className={classNames(
                      'w-full pt-2',
                      isLastQuestion ? 'mb-0' : '',
                    )}
                    answerClassName="font-medium"
                  />
                );
              case AssessmentQuestionType.FileUpload:
                const fileUploadAnswer = answer as FileUploadAssessmentAnswer;
                return (
                  <QuestionItem
                    key={questionIndex}
                    isForm={isForm}
                    questionIndex={questionIndex}
                    question={fileUploadAnswer.question}
                    className={classNames(
                      'w-full pt-2',
                      isLastQuestion ? 'mb-0' : '',
                    )}
                    answerClassName="font-normal"
                    subscale={
                      !fileUploadAnswer.fileName
                        ? QuestionSubscale.FileUpload
                        : undefined
                    }
                    additionalAnswerElem={
                      <FileInfoDisplay
                        fileName={fileUploadAnswer.fileName}
                        mediaId={fileUploadAnswer.mediaId}
                        mediaUrl={fileUploadAnswer.mediaUrl || undefined}
                      />
                    }
                  />
                );
              case AssessmentQuestionType.Autofill:
              case AssessmentQuestionType.DatePicker:
                const answerValue = getAnswerValue(answer);
                return (
                  <QuestionItem
                    key={questionIndex}
                    isForm={isForm}
                    questionIndex={questionIndex}
                    question={getAnswerQuestion(answer) ?? ''}
                    answer={answerValue ?? undefined}
                    subscale={
                      answerValue
                        ? undefined
                        : QuestionSubscale[
                            answer.questionType ===
                            AssessmentQuestionType.Autofill
                              ? 'Autofill'
                              : 'DatePicker'
                          ]
                    }
                    className={classNames(
                      'w-full pt-2',
                      isLastQuestion ? 'mb-0' : '',
                    )}
                    answerClassName="font-medium"
                  />
                );
              case AssessmentQuestionType.PatientContact: {
                const pcAnswer = answer as PatientContactAssessmentAnswer;
                const text = formatPatientContactSnapshotAnswer(
                  pcAnswer.contactSnapshot,
                );
                return (
                  <QuestionItem
                    key={questionIndex}
                    isForm={isForm}
                    questionIndex={questionIndex}
                    question={pcAnswer.question}
                    answer={text}
                    className={classNames(
                      'w-full pt-2',
                      isLastQuestion ? 'mb-0' : '',
                    )}
                    answerClassName="font-medium whitespace-pre-line"
                  />
                );
              }
              default:
                return null;
            }
          } else {
            // Handle FileUpload questions without answers
            if (
              templateQuestion?.questionType ===
              AssessmentQuestionType.FileUpload
            ) {
              return (
                <QuestionItem
                  key={questionIndex}
                  isForm={isForm}
                  questionIndex={questionIndex}
                  question={questionText}
                  subscale={QuestionSubscale.FileUpload}
                  className={classNames(
                    'w-full pt-2',
                    isLastQuestion && 'mb-0',
                  )}
                  additionalAnswerElem={
                    <FileInfoDisplay fileName={null} mediaId={null} />
                  }
                />
              );
            }
            return (
              <QuestionItem
                key={questionIndex}
                isForm={isForm}
                questionIndex={questionIndex}
                question={questionText}
                subscale={
                  templateQuestion &&
                  !isStatementAssessmentQuestion(templateQuestion) &&
                  QuestionSubscale[templateQuestion.questionType]
                }
                className={classNames('w-full pt-2', isLastQuestion && 'mb-0')}
              />
            );
          }
        },
      )}
    </SlideoverSection>
  );
};

export default ResponsesSection;
